import React, { useState, useEffect, useRef } from 'react';
import {
  Cog6ToothIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const AdminNavigation = () => {
  const [activeSection, setActiveSection] = useState('statistics');
  const observerRef = useRef(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const navigationItems = [
    { id: 'statistics', label: 'Statistics', icon: Cog6ToothIcon },
    { id: 'bulk-actions', label: 'Bulk Actions', icon: ClipboardDocumentListIcon },
    { id: 'pending-requests', label: 'Pending Requests', icon: ClipboardDocumentListIcon },
    { id: 'users', label: 'Users', icon: UsersIcon },
    { id: 'system-administrators', label: 'System Admins', icon: ShieldCheckIcon },
    { id: 'training-groups', label: 'Training Groups', icon: UserGroupIcon },
    { id: 'crews', label: 'Crews', icon: WrenchScrewdriverIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Add scroll-margin-top temporarily to handle offset
      const originalScrollMargin = element.style.scrollMarginTop;
      element.style.scrollMarginTop = '100px';

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Reset the scroll margin after a short delay
      setTimeout(() => {
        element.style.scrollMarginTop = originalScrollMargin;
      }, 1000);
    }
    setShowMobileMenu(false); // Always close mobile menu after navigation
  };

  // Set up intersection observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // Trigger when section is 20% from top and 70% from bottom
      threshold: 0,
    };

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, options);

    // Observe all sections
    navigationItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div
      className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 40,
        backgroundColor: 'white',
      }}
    >
      {/* Mobile nav bar */}
      <div className="flex items-center justify-between px-4 py-2 sm:hidden">
        <span className="text-base font-semibold text-gray-700">Admin Navigation</span>
        <button
          aria-label="Open navigation menu"
          className="rounded-md p-2 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-400"
          onClick={() => setShowMobileMenu((prev) => !prev)}
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            {showMobileMenu ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>
      {/* Mobile dropdown menu */}
      {showMobileMenu && (
        <div className="animate-fade-in-down border-t border-gray-200 bg-white shadow-md sm:hidden">
          <nav className="flex flex-col py-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`flex items-center space-x-2 px-4 py-3 text-left text-base font-medium transition-colors ${isActive
                      ? 'border-l-4 border-blue-400 bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      )}
      {/* Desktop nav bar */}
      <div className="hidden px-6 py-3 sm:block">
        <nav className="scrollbar-hide flex space-x-1 overflow-x-auto">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeSection === item.id;

            return (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`flex items-center space-x-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${isActive
                    ? 'border border-blue-200 bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <Icon className="h-4 w-4" />
                <span className="whitespace-nowrap">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default AdminNavigation;
